import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ETATS_INSCRIPTION, type DemandeInscriptionRegie } from '@artosera/shared';
import { Button, Checkbox, Dialog, Input, Select } from '@artosera/ui';
import {
  evenementsApi,
  useActionEvenement,
  useInscrits,
  type FiltresInscrits,
  type ResultatEmargement,
  type EvenementPresente,
  type InscriptionPresentee,
} from '../../api/evenements.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { ChoixContact } from '../pipeline/ChoixContact.js';
import styles from './Evenements.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

type GesteBillet = 'annuler' | 'confirmer' | 'renvoyer' | 'regenerer';
export function InscritsEvenement({
  evenement,
  accueil = false,
  onResultat,
}: {
  evenement: EvenementPresente;
  accueil?: boolean;
  onResultat?: (r: ResultatEmargement | null) => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [filtres, setFiltres] = useState<FiltresInscrits>({});
  const [nouvelle, setNouvelle] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    inscription: InscriptionPresentee;
    geste: GesteBillet;
  } | null>(null);
  const lire = galerieALaction(galerie, 'events:read');
  const ecrire =
    !accueil && evenement.etat !== 'annule' && galerieALaction(galerie, 'events:write');
  const pointer = galerieALaction(galerie, 'events:checkin');
  const liste = useInscrits(galerieSlug, evenement.id, filtres, lire);
  const billet = useActionEvenement(
    galerieSlug,
    ({ inscription, geste }: { inscription: InscriptionPresentee; geste: GesteBillet }, appel) =>
      evenementsApi.billet(galerieSlug, evenement.id, inscription.id, geste, appel),
  );
  const presence = useActionEvenement(galerieSlug, (i: InscriptionPresentee, appel) =>
    evenementsApi.presence(galerieSlug, evenement.id, i.id, !i.presentLe, appel),
  );
  const erreur = billet.error ?? presence.error;
  const occupe = billet.isPending || presence.isPending;
  function boutonPresence(i: InscriptionPresentee) {
    if (
      !pointer ||
      i.contactEfface ||
      (!i.presentLe && (i.etat !== 'confirmee' || evenement.etat === 'annule'))
    )
      return null;
    return (
      <Button
        variant="secondary"
        disabled={occupe}
        onClick={() =>
          presence.mutate(i, { onSuccess: (r) => onResultat?.('resultat' in r ? r : null) })
        }
      >
        {t(i.presentLe ? 'evenements.retirerPresence' : 'evenements.marquerPresent')}
      </Button>
    );
  }
  return (
    <div className={styles.formulaire}>
      <div className={styles.actions}>
        <Field htmlFor="evt-recherche" label={t('evenements.recherche')}>
          <Input
            id="evt-recherche"
            type="search"
            maxLength={200}
            value={filtres.q ?? ''}
            onChange={(e) => setFiltres({ ...filtres, q: e.target.value })}
          />
        </Field>
        {!accueil ? (
          <Field htmlFor="evt-etat" label={t('evenements.etat')}>
            <Select
              id="evt-etat"
              value={filtres.etat ?? ''}
              onValueChange={(v) =>
                setFiltres({ ...filtres, etat: (v || undefined) as FiltresInscrits['etat'] })
              }
              options={[
                { value: '', label: t('evenements.tous') },
                ...ETATS_INSCRIPTION.map((v) => ({ value: v, label: t(`evenements.etats.${v}`) })),
              ]}
            />
          </Field>
        ) : null}
        <Verrouillable action="events:write" ouvert={ecrire}>
          <Button onClick={() => setNouvelle(true)}>{t('evenements.inscrire')}</Button>
        </Verrouillable>
      </div>
      {liste.isPending ? <p role="status">{t('commun:chargement')}</p> : null}
      {liste.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {erreur ? (
        <p role="alert">{estApiError(erreur) ? erreur.message : t('commun:erreurReseau')}</p>
      ) : null}
      {presence.data && 'resultat' in presence.data && !onResultat ? (
        <p role="status">{t(`evenements.resultats.${presence.data.resultat}`)}</p>
      ) : null}
      {liste.data?.inscriptions.length === 0 ? <p>{t('evenements.aucunInscrit')}</p> : null}
      {accueil ? (
        <div className={styles.liste}>
          {liste.data?.inscriptions.map((i) => (
            <article key={i.id} className={styles.carte}>
              <h2>{i.nom || t('evenements.contactEfface')}</h2>
              <p>
                {t('evenements.nombrePlaces', { count: i.places })} ·{' '}
                {t(`evenements.etats.${i.etat}`)}
              </p>
              <p>
                {i.presentLe
                  ? new Date(i.presentLe).toLocaleString(i18n.language)
                  : t('evenements.absent')}
              </p>
              {boutonPresence(i)}
            </article>
          ))}
        </div>
      ) : (
        <div className={styles.tableau}>
          <table>
            <caption>{t('evenements.inscrits')}</caption>
            <thead>
              <tr>
                {['nom', 'places', 'etat', 'rang', 'presence', 'source', 'gestes'].map((c) => (
                  <th key={c} scope="col">
                    {t(`evenements.${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {liste.data?.inscriptions.map((i) => (
                <tr key={i.id}>
                  <th scope="row">{i.nom || t('evenements.contactEfface')}</th>
                  <td>{i.places}</td>
                  <td>{t(`evenements.etats.${i.etat}`)}</td>
                  <td>{i.rangAttente ?? '—'}</td>
                  <td>
                    {i.presentLe
                      ? new Date(i.presentLe).toLocaleString(i18n.language)
                      : t('evenements.absent')}
                  </td>
                  <td>{t(`evenements.sources.${i.source}`)}</td>
                  <td>
                    <div className={styles.actions}>
                      {ecrire && i.etat !== 'annulee' && !i.contactEfface ? (
                        <>
                          {i.etat === 'liste_attente' ? (
                            <Button
                              variant="secondary"
                              disabled={occupe}
                              onClick={() => billet.mutate({ inscription: i, geste: 'confirmer' })}
                            >
                              {t('evenements.gestesBillet.confirmer')}
                            </Button>
                          ) : null}
                          {(['annuler', 'renvoyer', 'regenerer'] as const).map((geste) => (
                            <Button
                              key={geste}
                              variant="secondary"
                              disabled={occupe}
                              onClick={() => {
                                if (geste === 'regenerer' || geste === 'annuler')
                                  setConfirmation({ inscription: i, geste });
                                else billet.mutate({ inscription: i, geste });
                              }}
                            >
                              {t(`evenements.gestesBillet.${geste}`)}
                            </Button>
                          ))}
                        </>
                      ) : null}
                      {boutonPresence(i)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nouvelle ? (
        <InscrireDialog evenement={evenement} onFermer={() => setNouvelle(false)} />
      ) : null}
      <Dialog
        open={!!confirmation}
        onOpenChange={(v) => {
          if (!v) setConfirmation(null);
        }}
        title={t(
          confirmation?.geste === 'regenerer'
            ? 'evenements.confirmationBillet'
            : 'evenements.confirmationAnnulationInscrit',
        )}
        description={confirmation?.inscription.nom}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            loading={billet.isPending}
            onClick={() => {
              if (confirmation)
                billet.mutate(confirmation, { onSuccess: () => setConfirmation(null) });
            }}
          >
            {t('evenements.confirmer')}
          </Button>
        }
      >
        <p>
          {t(
            confirmation?.geste === 'regenerer'
              ? 'evenements.ancienBilletInvalide'
              : 'evenements.placesLiberees',
          )}
        </p>
        {billet.isError ? (
          <p role="alert">
            {estApiError(billet.error) ? billet.error.message : t('commun:erreurReseau')}
          </p>
        ) : null}
      </Dialog>
    </div>
  );
}
function InscrireDialog({
  evenement,
  onFermer,
}: {
  evenement: EvenementPresente;
  onFermer: () => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const [mode, setMode] = useState('nouvelle');
  const [contact, setContact] = useState<{ id: string; nom: string } | null>(null);
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [places, setPlaces] = useState(1);
  const [forcer, setForcer] = useState(false);
  const inscription = useActionEvenement(galerieSlug, (corps: DemandeInscriptionRegie, appel) =>
    evenementsApi.inscrire(galerieSlug, evenement.id, corps, appel),
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onFermer();
      }}
      title={t('evenements.inscrire')}
      closeLabel={t('commun:actions.fermer')}
    >
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          if (mode === 'contact' && !contact) return;
          inscription.mutate(
            {
              ...(mode === 'contact' ? { contactId: contact!.id } : { nom, email, langue }),
              places,
              forcer,
            },
            { onSuccess: onFermer },
          );
        }}
      >
        <Select
          aria-label={t('evenements.modeInscription')}
          value={mode}
          onValueChange={setMode}
          options={[
            { value: 'nouvelle', label: t('evenements.personneNouvelle') },
            ...(galerieALaction(galerie, 'contacts:read')
              ? [{ value: 'contact', label: t('evenements.contactExistant') }]
              : []),
          ]}
        />
        {mode === 'contact' ? (
          <ChoixContact valeur={contact} onChange={setContact} />
        ) : (
          <>
            <Field htmlFor="inscrit-nom" label={t('evenements.nom')}>
              <Input
                id="inscrit-nom"
                required
                maxLength={200}
                value={nom}
                onChange={(e) => setNom(e.target.value)}
              />
            </Field>
            <Field htmlFor="inscrit-email" label={t('evenements.email')}>
              <Input
                id="inscrit-email"
                type="email"
                required
                maxLength={320}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
          </>
        )}
        <Field htmlFor="inscrit-places" label={t('evenements.places')}>
          <Input
            id="inscrit-places"
            type="number"
            required
            min={1}
            max={evenement.placesParInscriptionMax}
            step={1}
            value={places}
            onChange={(e) => setPlaces(Number(e.target.value))}
          />
        </Field>
        <Checkbox
          label={t('evenements.forcer')}
          checked={forcer}
          onCheckedChange={(v) => setForcer(v === true)}
        />
        <Button
          type="submit"
          loading={inscription.isPending}
          disabled={mode === 'contact' && !contact}
        >
          {t('evenements.inscrire')}
        </Button>
        {inscription.isError ? (
          <p role="alert">
            {estApiError(inscription.error) ? inscription.error.message : t('commun:erreurReseau')}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
